/**
 * A linked record inside an update, the way Linear lists related issues: one
 * row, a status glyph, the title, a short summary that clamps to two lines
 * with "more" beside it, and the state on the right. The row opens the
 * record; hovering shows a preview card and an "Open" hint; a decision
 * (Reject) sits at the end as a quiet control, never as a card footer.
 *
 * @module components/inbox/LinkedRecordRow
 */
import { useNavigate } from "@tanstack/react-router";
import {
  ArrowUpRightIcon,
  CircleAlertIcon,
  CircleCheckIcon,
  CircleDashedIcon,
  CircleDotIcon,
  CircleXIcon,
  MousePointerClickIcon,
  type LucideIcon,
} from "lucide-react";
import { useLayoutEffect, useRef, useState, type ReactNode } from "react";

import { cn } from "../../lib/utils";
import { HoverCard, HoverCardPopup, HoverCardTrigger } from "../ui/hover-card";
import { Skeleton } from "../ui/skeleton";

export type LinkedRecordState =
  | "proposed"
  | "in-progress"
  | "applied"
  | "rejected"
  | "open"
  | "resolved"
  | "closed";

/** One glyph and one colour per state, shared by suggestions and findings. */
export const LINKED_RECORD_STATE: Record<
  LinkedRecordState,
  { icon: LucideIcon; label: string; className: string }
> = {
  proposed: { icon: CircleDashedIcon, label: "Proposed", className: "text-muted-foreground" },
  "in-progress": { icon: CircleDotIcon, label: "In progress", className: "text-primary" },
  applied: { icon: CircleCheckIcon, label: "Applied", className: "text-success-foreground" },
  rejected: { icon: CircleXIcon, label: "Rejected", className: "text-muted-foreground" },
  open: { icon: CircleAlertIcon, label: "Open", className: "text-destructive-foreground" },
  resolved: { icon: CircleCheckIcon, label: "Resolved", className: "text-success-foreground" },
  closed: { icon: CircleXIcon, label: "Closed", className: "text-muted-foreground" },
};

export function suggestionRecordState(
  status: "open" | "accepted" | "applied" | "dismissed",
): LinkedRecordState {
  switch (status) {
    case "open":
      return "proposed";
    case "accepted":
      return "in-progress";
    case "applied":
      return "applied";
    case "dismissed":
      return "rejected";
  }
}

export function findingRecordState(status: "open" | "resolved" | "closed"): LinkedRecordState {
  return status;
}

export function LinkedRecordStateGlyph({ state, className }: { state: LinkedRecordState; className?: string }) {
  const { icon: Icon, label, className: tone } = LINKED_RECORD_STATE[state];
  return (
    <span role="img" aria-label={label} title={label} className={cn("flex size-4 shrink-0 items-center justify-center", className)}>
      <Icon aria-hidden className={cn("size-3.5", tone)} strokeWidth={2} />
    </span>
  );
}

/**
 * Two lines of text, then "more" beside the last line when there is more;
 * "less" folds it back. The toggle only appears when the text really
 * overflows, measured after layout.
 */
export function ClampedText({ children, className }: { children: string; className?: string }) {
  const ref = useRef<HTMLParagraphElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element || expanded) return;
    // Measured now and again whenever the column changes width: a narrower
    // pane can push a second line under the clamp, a wider one can free it.
    const measure = () => setOverflows(element.scrollHeight > element.clientHeight + 1);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [children, expanded]);
  return (
    <div className={cn("flex items-end gap-2", className)}>
      <p ref={ref} className={cn("min-w-0 flex-1", !expanded && "line-clamp-2")}>
        {children}
      </p>
      {(overflows || expanded) && (
        <button
          type="button"
          className="shrink-0 cursor-pointer text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
          onClick={(event) => {
            // Inside a clickable row: expanding must not open the record.
            event.stopPropagation();
            setExpanded((value) => !value);
          }}
        >
          {expanded ? "less" : "more"}
        </button>
      )}
    </div>
  );
}

export function LinkedRecordRow({
  state,
  title,
  summary,
  to,
  openLabel,
  trailing,
}: {
  readonly state: LinkedRecordState;
  readonly title: string;
  readonly summary?: string | undefined;
  /** Where the row goes when clicked. */
  readonly to: { readonly to: string; readonly params: Record<string, string> };
  /** "Open suggestion", "Open finding": the hint shown on hover. */
  readonly openLabel: string;
  /** A decision control shown at the row's end on hover, if the record has one. */
  readonly trailing?: ReactNode;
}) {
  const navigate = useNavigate();
  const open = () => void navigate(to as never);
  const { label: stateLabel, className: stateTone, icon: StateIcon } = LINKED_RECORD_STATE[state];
  return (
    <HoverCard>
      <HoverCardTrigger
        delay={450}
        closeDelay={80}
        render={
          // The row is the control: click or Enter opens the record. The
          // decision at its end is its own button and stops the click there.
          <div
            role="link"
            tabIndex={0}
            aria-label={`${openLabel}: ${title}`}
            onClick={open}
            onKeyDown={(event) => {
              // Only the row itself: Enter on a control inside it (Reject)
              // stays with that control.
              if (event.key === "Enter" && event.target === event.currentTarget) open();
            }}
            className="group/record -mx-2.5 flex cursor-pointer items-start gap-3 rounded-md px-2.5 py-2 outline-none transition-colors hover:bg-foreground/[0.05] focus-visible:ring-2 focus-visible:ring-ring"
          />
        }
      >
        <LinkedRecordStateGlyph state={state} className="mt-0.5" />
        <div className="min-w-0 flex-1">
          <span className="block truncate text-[13px] font-medium leading-5 text-foreground">{title}</span>
          {summary ? (
            <ClampedText className="mt-0.5 text-xs leading-5 text-muted-foreground">{summary}</ClampedText>
          ) : null}
        </div>
        {/* At rest the text has the whole row; the decision and the "Open"
            hint step in on hover or focus, the state itself is the glyph. */}
        <div
          className="hidden shrink-0 items-center gap-1 pt-0.5 group-hover/record:flex group-focus-within/record:flex"
          onClick={(event) => event.stopPropagation()}
        >
          {trailing}
          <span aria-hidden className="flex items-center gap-0.5 pl-1 text-xs text-muted-foreground">
            Open
            <ArrowUpRightIcon className="size-3" />
          </span>
        </div>
      </HoverCardTrigger>
      <HoverCardPopup side="top" align="start" sideOffset={6} className="w-[26rem] px-4 py-3.5">
        <p className={cn("flex items-center gap-1.5 text-xs font-medium", stateTone)}>
          <StateIcon aria-hidden className="size-3.5" strokeWidth={2} />
          {stateLabel}
        </p>
        <p className="mt-1.5 text-[13px] font-medium leading-5">{title}</p>
        {summary ? <p className="mt-1.5 line-clamp-6 text-xs leading-5 text-muted-foreground">{summary}</p> : null}
        <p className="mt-3 flex items-center gap-1 text-xs text-muted-foreground">
          <MousePointerClickIcon aria-hidden className="size-3.5" />
          {openLabel}
        </p>
      </HoverCardPopup>
    </HoverCard>
  );
}

/** The section heading: what these are, and how many. */
export function LinkedRecordHeading({ label, count }: { label: string; count: number }) {
  return (
    <p className="mb-2 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
      <span>{label}</span>
      <span className="tabular-nums">{count}</span>
    </p>
  );
}

export function LinkedRecordSkeleton({ rows }: { rows: number }) {
  return (
    <div className="space-y-2" aria-hidden>
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="flex items-start gap-3 py-2">
          <Skeleton className="mt-0.5 size-4 rounded-full" />
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-3.5 w-2/3" />
            <Skeleton className="h-3 w-full" />
          </div>
        </div>
      ))}
    </div>
  );
}
